import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Plus, Loader2, Edit2, Trash2, Tag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { API_BASE as API } from '../../config/api';

// Common inflection categories by POS type
const COMMON_CATEGORIES = {
  noun: ["number", "case", "gender", "definiteness", "possession"],
  verb: ["tense", "aspect", "mood", "person", "number", "voice", "evidentiality", "polarity"],
  adjective: ["number", "gender", "case", "degree"],
  pronoun: ["person", "number", "gender", "case"],
  determiner: ["number", "gender", "case", "definiteness"],
  adverb: ["degree"],
};

const DEFAULT_POS = [
  { name: "Noun", abbreviation: "n", suggested: "noun" },
  { name: "Verb", abbreviation: "v", suggested: "verb" },
  { name: "Adjective", abbreviation: "adj", suggested: "adjective" },
  { name: "Adverb", abbreviation: "adv", suggested: "adverb" },
  { name: "Pronoun", abbreviation: "pron", suggested: "pronoun" },
  { name: "Determiner", abbreviation: "det", suggested: "determiner" },
  { name: "Preposition", abbreviation: "prep", suggested: null },
  { name: "Conjunction", abbreviation: "conj", suggested: null },
  { name: "Interjection", abbreviation: "interj", suggested: null },
];

const PartsOfSpeechPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const [partsOfSpeech, setPartsOfSpeech] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPOS, setEditingPOS] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [description, setDescription] = useState('');
  const [hasInflection, setHasInflection] = useState(false);
  const [inflectionCategories, setInflectionCategories] = useState([]);
  const [suggestedType, setSuggestedType] = useState(null);

  useEffect(() => {
    fetchPartsOfSpeech();
  }, [conlang?.conlang_id]);

  const fetchPartsOfSpeech = async () => {
    if (!conlang?.conlang_id) return;
    try {
      const response = await axios.get(`${API}/conlangs/${conlang.conlang_id}/parts-of-speech`, {
        withCredentials: true
      });
      setPartsOfSpeech(response.data);
    } catch (error) {
      toast.error('Failed to load parts of speech');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setAbbreviation('');
    setDescription('');
    setHasInflection(false);
    setInflectionCategories([]);
    setSuggestedType(null);
    setEditingPOS(null);
  };

  const openCreateDialog = (defaultPOS = null) => {
    resetForm();
    if (defaultPOS) {
      setName(defaultPOS.name);
      setAbbreviation(defaultPOS.abbreviation);
      setSuggestedType(defaultPOS.suggested);
    }
    setDialogOpen(true);
  };

  const openEditDialog = (pos) => {
    setEditingPOS(pos);
    setName(pos.name);
    setAbbreviation(pos.abbreviation);
    setDescription(pos.description || '');
    setHasInflection(pos.has_inflection);
    setInflectionCategories(pos.inflection_categories || []);
    // Try to match suggested type
    const match = Object.keys(COMMON_CATEGORIES).find(
      k => k.toLowerCase() === pos.name.toLowerCase()
    );
    setSuggestedType(match || null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    const data = {
      name,
      abbreviation,
      description: description || null,
      has_inflection: hasInflection,
      inflection_categories: hasInflection ? inflectionCategories : []
    };

    try {
      if (editingPOS) {
        const response = await axios.put(
          `${API}/conlangs/${conlang.conlang_id}/parts-of-speech/${editingPOS.pos_id}`,
          data,
          { withCredentials: true }
        );
        setPartsOfSpeech(partsOfSpeech.map(p => 
          p.pos_id === editingPOS.pos_id ? response.data : p
        ));
        toast.success('Part of speech updated');
      } else {
        const response = await axios.post(
          `${API}/conlangs/${conlang.conlang_id}/parts-of-speech`,
          data,
          { withCredentials: true }
        );
        setPartsOfSpeech([...partsOfSpeech, response.data]);
        toast.success('Part of speech created');
      }
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save part of speech');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (pos) => {
    if (!window.confirm(`Delete "${pos.name}"? This cannot be undone.`)) return;
    
    try {
      await axios.delete(
        `${API}/conlangs/${conlang.conlang_id}/parts-of-speech/${pos.pos_id}`,
        { withCredentials: true }
      );
      setPartsOfSpeech(partsOfSpeech.filter(p => p.pos_id !== pos.pos_id));
      toast.success('Part of speech deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleCategory = (category) => {
    setInflectionCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const suggestedCategories = suggestedType ? COMMON_CATEGORIES[suggestedType] || [] : [];

  // Get unused default POS for quick add
  const usedNames = partsOfSpeech.map(p => p.name.toLowerCase());
  const availableDefaults = DEFAULT_POS.filter(d => !usedNames.includes(d.name.toLowerCase()));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground">Parts of Speech</h2>
          <p className="text-foreground-muted mt-1">
            Define the grammatical categories in {conlang?.name}
          </p>
        </div>
        <Button onClick={() => openCreateDialog()} className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" />
          Add Part of Speech
        </Button>
      </div>

      {/* Quick Add Buttons */}
      {availableDefaults.length > 0 && partsOfSpeech.length === 0 && (
        <Card className="bg-background-paper border-border">
          <CardHeader>
            <CardTitle className="text-lg">Quick Start</CardTitle>
            <CardDescription>Add common parts of speech to get started</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {availableDefaults.map(def => (
                <Button
                  key={def.name}
                  variant="outline"
                  size="sm"
                  onClick={() => openCreateDialog(def)}
                  className="border-border"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {def.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Parts of Speech List */}
      {partsOfSpeech.length === 0 ? (
        <Card className="bg-background-paper border-border">
          <CardContent className="py-12 text-center">
            <Tag className="w-12 h-12 text-foreground-subtle mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              No parts of speech defined
            </h3>
            <p className="text-foreground-muted mb-4">
              Start by defining the grammatical categories your language uses.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {partsOfSpeech.map(pos => (
            <Card key={pos.pos_id} className="bg-background-paper border-border">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg text-foreground">{pos.name}</CardTitle>
                    <p className="text-sm text-secondary font-mono">{pos.abbreviation}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(pos)}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(pos)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {pos.description && (
                  <p className="text-sm text-foreground-muted mb-3">{pos.description}</p>
                )}
                {pos.has_inflection ? (
                  <div>
                    <p className="text-xs text-primary font-medium mb-2">Has inflection:</p>
                    <div className="flex flex-wrap gap-1">
                      {pos.inflection_categories.map(cat => (
                        <span 
                          key={cat} 
                          className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-foreground-subtle">No inflection</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add remaining defaults */}
      {partsOfSpeech.length > 0 && availableDefaults.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm text-foreground-muted">Quick add:</span>
          {availableDefaults.slice(0, 5).map(def => (
            <Button
              key={def.name}
              variant="outline"
              size="sm"
              onClick={() => openCreateDialog(def)}
              className="border-border"
            >
              <Plus className="w-3 h-3 mr-1" />
              {def.name}
            </Button>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background-paper border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {editingPOS ? 'Edit Part of Speech' : 'Add Part of Speech'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Noun"
                  className="bg-background border-border"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="abbreviation">Abbreviation</Label>
                <Input
                  id="abbreviation"
                  value={abbreviation}
                  onChange={(e) => setAbbreviation(e.target.value)}
                  placeholder="e.g., n"
                  className="bg-background border-border font-mono"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this part of speech in your language..."
                className="bg-background border-border"
                rows={2}
              />
            </div>

            <div className="space-y-4 p-4 bg-accent/30 rounded-lg">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasInflection"
                  checked={hasInflection}
                  onCheckedChange={setHasInflection}
                />
                <Label htmlFor="hasInflection" className="text-foreground cursor-pointer">
                  This part of speech has inflection
                </Label>
              </div>

              {hasInflection && (
                <div className="space-y-3 pt-2">
                  <Label className="text-foreground-muted">
                    Which categories does your language mark?
                  </Label>
                  
                  {suggestedCategories.length > 0 && (
                    <div>
                      <p className="text-xs text-foreground-subtle mb-2">Common for {suggestedType}s:</p>
                      <div className="flex flex-wrap gap-2">
                        {suggestedCategories.map(cat => (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => toggleCategory(cat)}
                            className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                              inflectionCategories.includes(cat)
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background border-border text-foreground-muted hover:border-primary'
                            }`}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Custom category input */}
                  <div className="pt-2">
                    <p className="text-xs text-foreground-subtle mb-2">Selected categories:</p>
                    <div className="flex flex-wrap gap-1">
                      {inflectionCategories.length === 0 ? (
                        <span className="text-xs text-foreground-subtle italic">None selected</span>
                      ) : (
                        inflectionCategories.map(cat => (
                          <span 
                            key={cat}
                            className="text-xs bg-primary/20 text-primary px-2 py-1 rounded flex items-center gap-1"
                          >
                            {cat}
                            <button
                              type="button"
                              onClick={() => toggleCategory(cat)}
                              className="hover:text-destructive"
                            >
                              ×
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={formLoading || !name.trim() || !abbreviation.trim()}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingPOS ? 'Save' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartsOfSpeechPage;
